import { inject, Inject, Injectable } from "@angular/core";
import { DOCUMENT } from "@angular/common";
import { Market } from "../types/bet.type";
import { environment } from "../../environments/environment";
import { BehaviorSubject, Subscription } from "rxjs";
import { ApiService } from "./api.service";
import { DomainService } from "./domain.service";
import { HttpClient } from "@angular/common/http";
import { PRE_MATCH_PLACEMENT_WINDOW_MINUTES } from "../constants/placement-window";
import { ThemeColorService } from "./theme-color.service";

type MarketConfig = { DESCRIPTION: string; IS_ACTIVE: boolean; NAME: string };
type UiSkinOption = { key: "v1" | "v2" | "v3"; skinKey: "classic" | "exchange_compact" | "v3"; label: string };

@Injectable({
  providedIn: "root",
})
export class WhiteLabelConfigService {
  http = inject(HttpClient);
  apiService = inject(ApiService);
  readonly domainService = inject(DomainService);
  readonly themeColorService = inject(ThemeColorService);
  subscriptions: Subscription = new Subscription();
  readonly marketConfig$ = new BehaviorSubject<Record<string, any>>({});
  readonly wlConfig$ = new BehaviorSubject<Record<string, any>>({});
  private readonly skinKeyStoragePrefix = "UI_SKIN_KEY";
  private adminUiSkinKey = "classic";
  readonly uiSkinOptions: readonly UiSkinOption[] = [
    { key: "v1", skinKey: "classic", label: "V1 Theme" },
    { key: "v2", skinKey: "exchange_compact", label: "V2 Theme" },
    { key: "v3", skinKey: "v3", label: "V3 Theme" },
  ];
  readonly uiSkinKey$ = new BehaviorSubject<string>(this.adminUiSkinKey);

  constructor(@Inject(DOCUMENT) private readonly document: Document) {
    this.uiSkinKey$.subscribe((key) => {
      this.applySkinClass(key);
    });
    this.subscriptions.add(this.apiService.$userTokenUpdated?.subscribe(() => this.resolveUiSkinKey()));
  }

  get uiSkinKey(): string {
    return this.uiSkinKey$.value;
  }

  setUiSkinKey(key: string): void {
    this.adminUiSkinKey = this.normalizeSkinKey(key);
    this.resolveUiSkinKey();
  }

  setUserUiSkinKey(key: string): boolean {
    if (!this.canUserSelectTheme()) return false;

    const option = this.uiSkinOptions.find((item) => item.key === key.toLowerCase().trim());
    const storageKey = this.getUserSkinStorageKey();
    if (!option || !storageKey || option.skinKey === this.uiSkinKey) return false;

    localStorage.setItem(storageKey, option.key);
    this.uiSkinKey$.next(option.skinKey);
    return true;
  }

  canUserSelectTheme(): boolean {
    const permissions = this.permissions;
    if (Array.isArray(permissions)) return permissions.includes("enableUserThemeSelection");
    if (typeof permissions !== "string") return false;
    return permissions
      .split(",")
      .map((permission) => permission.trim())
      .includes("enableUserThemeSelection");
  }

  isUiSkinActive(key: string): boolean {
    const option = this.uiSkinOptions.find((item) => item.key === key);
    return option?.skinKey === this.uiSkinKey;
  }

  private resolveUiSkinKey(): void {
    const storageKey = this.getUserSkinStorageKey();
    const storedKey = storageKey && this.canUserSelectTheme() ? localStorage.getItem(storageKey) : null;
    const option = this.uiSkinOptions.find((item) => item.key === storedKey);
    this.uiSkinKey$.next(option?.skinKey ?? this.adminUiSkinKey);
  }

  private getUserSkinStorageKey(): string | null {
    const user = this.apiService.getUserToken()?.user;
    if (!user?.DOMAIN_ID || !user?.ID) return null;
    return `${this.skinKeyStoragePrefix}:${user.DOMAIN_ID}:${user.ID}`;
  }

  private normalizeSkinKey(key: string | null | undefined): string {
    if (!key) return "classic";
    const lowerKey = key.toLowerCase().trim();
    if (lowerKey === "v1") return "classic";
    if (lowerKey === "v2") return "exchange_compact";
    return lowerKey;
  }

  private applySkinClass(key: string): void {
    if (!this.document?.body) return;
    const lowerKey = key ? key.toLowerCase() : "classic";

    // Clean up any dynamically set skin-*-active classes to avoid conflicts
    const bodyClasses = this.document.body.classList;
    const classesToRemove: string[] = [];
    bodyClasses.forEach((cls) => {
      if (cls.startsWith("skin-") && cls.endsWith("-active")) {
        classesToRemove.push(cls);
      }
    });
    classesToRemove.forEach((cls) => bodyClasses.remove(cls));

    // Add the active class for the current normalized skin key
    bodyClasses.add(`skin-${lowerKey}-active`);

    // Keep backward-compatible legacy classes and colors for exchange_compact (v2)
    const isExchangeCompact = lowerKey === "exchange_compact" || lowerKey === "redesigned" || lowerKey === "v2_header" || lowerKey === "v2_sports_bar";

    if (isExchangeCompact) {
      bodyClasses.add("skin-v2-active", "skin-exchange-compact-active");
      if (!this.themeColorService.hasStoredTheme()) {
        this.themeColorService.setThemeColor("classic-blue");
      }
    }

    if (lowerKey === "v3") {
      this.themeColorService.setThemeColor("amber-charcoal");
    }
  }

  get whiteLabelId() {
    return this.apiService.getUserToken()?.user?.DOMAIN_ID;
  }

  get permissions() {
    return this.apiService.getUserToken()?.user?.PERMISSIONS;
  }

  updateFavicon(faviconPath: string): void {
    let link: HTMLLinkElement = this.document.querySelector("link[rel*='icon']") || this.document.createElement("link");
    link.type = "image/x-icon";
    link.rel = "shortcut icon";
    link.href = faviconPath;
    this.document.getElementsByTagName("head")[0].appendChild(link);
  }

  updateTitle(title: string): void {
    this.document.title = title;
  }

  showLayBet(market: Market) {
    const eventType = String(market?.eventType?.id);
    const isGreyhound = ["2147483999", "4339"].includes(eventType);
    const isHorseRacing = ["2147483666", "7"].includes(eventType);

    if (isGreyhound) return this.showFeature("greyHoundRacingLayBet");
    if (isHorseRacing) return this.showFeature("horseRacingLayBet");
    return true;
  }

  showFeature(permission: string) {
    return this.permissions?.includes(permission);
  }

  initAppSequence(): Promise<void> {
    return new Promise<void>((resolve) => {
      const domain = this.domainService.getDomain();
      if (!domain) {
        resolve();
        return;
      }

      this.apiService.fetchWlPermissions(domain).subscribe({
        next: (response: any) => {
          const config = response?.result?.[0];
          if (config) {
            this.wlConfig$.next(config);
            if (config.UI_SKIN_KEY) {
              this.setUiSkinKey(config.UI_SKIN_KEY);
            }
            this.updateFavicon(`${this.domainService.wlAssetURL}/favicon.ico`);
            this.updateTitle(this.domainService.pageTitle);
          }

          const existingToken = this.apiService.getUserToken();
          if (existingToken) {
            resolve();
          } else {
            this.apiService
              .performLogin("", "", "demo")
              .then((success) => {
                if (success) {
                  this.apiService.$isAuthenticated.next(true);
                }
                resolve();
              })
              .catch((err) => {
                console.error("Silent guest login failed:", err);
                resolve();
              });
          }
        },
        error: (err) => {
          console.error("Failed to fetch white label config on bootstrap:", err);
          resolve();
        },
      });
    });
  }

  fetchWlMarketConfig() {
    const domain = this.apiService.getUserToken()?.user?.DOMAIN_NAME || this.domainService.getDomain();
    if (domain) {
      this.subscriptions.add(
        this.apiService.fetchWlPermissions(domain).subscribe({
          next: (response: any) => {
            const config = response?.result?.[0];
            if (config?.UI_SKIN_KEY) {
              this.setUiSkinKey(config.UI_SKIN_KEY);
            }
          },
          error: () => {},
        }),
      );
    }

    this.subscriptions.add(
      this.http.get(`${environment.sURL}${environment.endpoints.wlMarketConfig}/${this.whiteLabelId}`).subscribe({
        next: (response: any) => {
          const result: MarketConfig[] = response?.result;
          if (result) {
            const config = result?.reduce(
              (acc, item) => {
                acc[item.NAME] = item.IS_ACTIVE;
                return acc;
              },
              {} as Record<string, boolean>,
            );
            this.marketConfig$.next({ ...config, PRE_MATCH_PLACEMENT: true, UPDATE_UNMATCHED_BET: true, LINE_MARKET: true });
          }
        },
      }),
    );
  }

  isWithinPreMatchPlacementWindow(marketStartTime: string): boolean {
    const eventTime = new Date(marketStartTime).getTime();
    const now = Date.now();

    const diffInMs = eventTime - now;
    const diffInMinutes = diffInMs / (1000 * 60);

    return diffInMinutes <= PRE_MATCH_PLACEMENT_WINDOW_MINUTES;
  }
}
